"use client";

import { CalendarDays, ChevronLeft, Ticket, Users } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { toast } from "sonner";
import {
  cancelJoinRequest,
  createJoinRequest,
  joinDirect,
} from "@/actions/tier";
import { useAuthPrompt } from "@/components/auth";
import { PhoneVerificationDrawer } from "@/components/auth/phone-verification-drawer";
import { ContactEmailDrawer } from "@/components/contact-email/contact-email-drawer";
import { ConfettiEffect } from "@/components/ui/confetti";
import Link from "@/components/ui/link";
import { useCurrentHref, useResolvedBackHref } from "@/hooks/use-current-href";
import { useScreenView } from "@/hooks/use-screen-view";
import { useSwipeBackTarget } from "@/hooks/use-swipe-back-target";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import {
  consumeCheckoutIntent,
  stashCheckoutIntent,
} from "@/lib/checkout-intent";
import { goBackTo } from "@/lib/go-back";
import { createClientLogger } from "@/lib/logging/client";
import { PHONE_VERIFICATION_REQUIRED } from "@/lib/payments/verified-payer-contract";
import { getRazorpaySubscriptionCheckout } from "@/lib/razorpay-subscription-checkout";
import type { TierWithPrices } from "@/validations/tier";
import type { MemberNextStepsData } from "./member-next-steps-actions";
import {
  FreeMemberView,
  PaidMemberView,
  PendingMemberView,
} from "./membership-status-views";
import { TierCard } from "./tier-card";
import { TierRequestSheet } from "./tier-request-sheet";

const logger = createClientLogger(
  "components/tier/selection/tier-selection-page",
);

type MembershipState =
  | "not_member"
  | "free_member"
  | "free_member_upgrade_available"
  | "payment_pending"
  | "paid_member"
  | "pending_member";

type JoinOutcome = "joined" | "requested" | "payment" | "failed";

type PendingPhoneVerification = {
  tierId: number;
  tierPriceId: number;
};

const requiresPhoneVerification = (result: unknown) =>
  Boolean(
    result &&
      typeof result === "object" &&
      "code" in result &&
      result.code === PHONE_VERIFICATION_REQUIRED,
  );

/** The server resolved this to a request that already existed. */
const alreadyPending = (result: unknown) =>
  Boolean(
    result &&
      typeof result === "object" &&
      "alreadyPending" in result &&
      result.alreadyPending === true,
  );

/** The request was created but the optional note could not be reviewed. */
const noteDropped = (result: unknown) =>
  Boolean(
    result &&
      typeof result === "object" &&
      "noteDropped" in result &&
      result.noteDropped === true,
  );

/** A pending request on this hiive, for a tier other than the one tapped. */
const existingRequestId = (result: unknown) =>
  result &&
  typeof result === "object" &&
  "existingRequestId" in result &&
  typeof result.existingRequestId === "number"
    ? result.existingRequestId
    : null;

/**
 * How long the hand-off may stay silent before the cover offers a way out.
 *
 * Long enough that an ordinary slow network never sees it, short enough that
 * nobody sits in front of a spinner wondering whether the app has died.
 */
const HANDOFF_STALL_MS = 12_000;

export type JoinEventContext = {
  eventId: number;
  eventName: string;
  eventSlug: string;
  eventHref: string;
  bannerUrl: string | null;
  whenLabel: string | null;
  requiredTierIds: number[];
  requiredTierNames: string[];
};

type TierJoinIntent = { tierId: number; tierPriceId: number };

interface PendingJoinRequestSummary {
  id: number;
  tierName: string;
  requestedAt: string;
  expiresAt: string | null;
}

interface TierSelectionPageProps {
  hiiveId: number;
  hiiveSlug: string;
  hiiveName: string;
  tiers: TierWithPrices[];
  membershipState: MembershipState;
  pendingJoinRequest?: PendingJoinRequestSummary | null;
  currentMembership?: {
    id: number;
    membershipId: string;
    tierId: number;
    tierPriceId: number;
    subscriptionStatus: string;
  };
  isAuthenticated?: boolean;
  /** Tiers are a social surface: they need a finished profile, not just a session. */
  profileComplete?: boolean;
  /** False when we have no verified address to send the approval to. */
  hasDeliverableEmail?: boolean;
  /** That address, so the confirm sheet can name where the answer is going. */
  contactEmail?: string | null;
  /** Set when the viewer arrived from a tier-gated event. */
  eventContext?: JoinEventContext | null;
  /**
   * Where a member can go from here. Null for anyone who is not one yet — it is
   * only ever read by the two "you're in" screens.
   */
  nextSteps?: MemberNextStepsData | null;
  previewMode?: boolean;
}

export function TierSelectionPage({
  hiiveId,
  hiiveSlug,
  hiiveName,
  tiers,
  membershipState,
  pendingJoinRequest,
  currentMembership,
  isAuthenticated = true,
  profileComplete = true,
  hasDeliverableEmail = true,
  contactEmail = null,
  eventContext = null,
  nextSteps = null,
  previewMode = false,
}: TierSelectionPageProps) {
  useScreenView(ANALYTICS_EVENTS.TIER_VIEWED, hiiveId, {
    hiive_id: hiiveId,
    hiive_slug: hiiveSlug,
    tier_count: tiers.length,
    membership_state: membershipState,
    preview_mode: previewMode,
  });

  const router = useRouter();
  const backHref = useResolvedBackHref(`/hiives/${hiiveSlug}`);
  const backRef = useRef<HTMLAnchorElement>(null);
  const onBack = () => {
    captureClientEvent(ANALYTICS_EVENTS.TIER_SELECTION_NAVIGATED_BACK, {
      hiiveId,
      hiiveSlug,
      membershipState,
    });
    goBackTo(router, backHref);
  };
  useSwipeBackTarget(backRef, onBack);
  const currentHref = useCurrentHref();
  const { openAuthPrompt } = useAuthPrompt();
  const [showConfetti, setShowConfetti] = useState(false);
  const [pendingPhoneVerification, setPendingPhoneVerification] =
    useState<PendingPhoneVerification | null>(null);
  const [isCancelling, startCancelling] = useTransition();
  /** The approval-gated tier awaiting its confirm sheet. */
  const [requestSheet, setRequestSheet] = useState<
    (TierJoinIntent & { tierName: string }) | null
  >(null);
  /*
   * `useTransition`, not `useState`.
   *
   * The old flag was set before the server action and cleared in its
   * `.finally`, which made the action promise the ONLY thing that could ever
   * end the loading state — so the one time the action was slow, "Sending"
   * was the last thing the person saw. React's own transition state is
   * self-clearing and, because `router.push` is started inside the
   * transition, it stays true across the navigation too: the cover is up for
   * the whole hand-off rather than lifting between the two halves of it.
   */
  const [isSendingRequest, startSendingRequest] = useTransition();
  /**
   * Raised when the hand-off has run long enough that silence is no longer
   * credible. It does not cancel anything — it just stops the screen being a
   * dead end, because a cover with no way out is worse than the wait.
   */
  const [handoffStalled, setHandoffStalled] = useState(false);
  /**
   * Where to go once the post-request email ask is done with. Non-null only
   * while that drawer is open, so it doubles as the drawer's `open` flag.
   */
  const [pendingEmailDestination, setPendingEmailDestination] = useState<
    string | null
  >(null);
  /** Guards the two callbacks that both fire when the email drawer closes. */
  const emailHandoffDone = useRef(false);
  const gridRef = useRef<HTMLDivElement | null>(null);

  /**
   * The failsafe. Derived from the transition, so it can never outlive the
   * thing it describes: when the hand-off ends, the timer is cleared and the
   * stalled state resets on its own.
   */
  useEffect(() => {
    if (!isSendingRequest) {
      setHandoffStalled(false);
      return;
    }
    const timer = window.setTimeout(
      () => setHandoffStalled(true),
      HANDOFF_STALL_MS,
    );
    return () => window.clearTimeout(timer);
  }, [isSendingRequest]);

  const requiredTierIds = useMemo(
    () => new Set(eventContext?.requiredTierIds ?? []),
    [eventContext],
  );

  /**
   * Required tiers first when there is an event in play.
   *
   * The scroll alone is not enough on a phone: land somebody mid-grid and the
   * cards above are invisible history they have to scroll up to discover.
   * Reordering means the answer is simply the first thing on the page, and the
   * scroll becomes a nicety rather than the only signal. Ordering is stable
   * within each group, so the organiser's own sort order still shows through.
   */
  const orderedTiers = useMemo(() => {
    if (!eventContext) return tiers;
    const required = tiers.filter((tier) => requiredTierIds.has(tier.id));
    const rest = tiers.filter((tier) => !requiredTierIds.has(tier.id));
    return [...required, ...rest];
  }, [eventContext, requiredTierIds, tiers]);

  /**
   * The intent slot is keyed per hiive, so an abandoned pick on one hiive can
   * never resume on another.
   */
  const intentKey = `${hiiveSlug}:tier-join`;

  const runJoin = useCallback(
    async (
      tierId: number,
      tierPriceId: number,
      reason?: string,
    ): Promise<JoinOutcome> => {
      const selectedTier = tiers.find((tier) => tier.id === tierId);
      if (!selectedTier) {
        toast.error("Selected tier not found");
        captureClientEvent(ANALYTICS_EVENTS.TIER_JOIN_FAILED, {
          hiiveId,
          hiiveSlug,
          tierId,
          tierPriceId,
          reason: "tier_not_found",
        });
        return "failed";
      }

      const requiresApproval = selectedTier.requiresApproval;
      const analyticsProperties = {
        hiiveId,
        hiiveSlug,
        tierId,
        tierPriceId,
        requiresApproval,
        membershipState,
      };

      try {
        captureClientEvent(
          ANALYTICS_EVENTS.TIER_JOIN_ATTEMPTED,
          analyticsProperties,
        );

        // `forEventSlug` lets the approval point back at what motivated the
        // request; `reason` is the requester's optional note. Both are
        // server-validated before they are trusted — see
        // `validateJoinRequestOrigin` and the moderation pass beside it.
        const metadata = {
          ...(eventContext ? { forEventSlug: eventContext.eventSlug } : {}),
          ...(reason ? { reason } : {}),
        };

        const payload = {
          hiiveId,
          tierId,
          tierPriceId,
          ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
        };

        const result = requiresApproval
          ? await createJoinRequest({
              hiiveId: payload.hiiveId,
              tierId: payload.tierId,
              tierPriceId: payload.tierPriceId,
              ...("metadata" in payload ? { metadata: payload.metadata } : {}),
            })
          : await joinDirect(payload);

        if (!result.ok) {
          captureClientEvent(ANALYTICS_EVENTS.TIER_JOIN_FAILED, {
            ...analyticsProperties,
            reason: result.message ?? "unknown",
          });
          if (requiresPhoneVerification(result)) {
            setPendingPhoneVerification({ tierId, tierPriceId });
            return "failed";
          }

          /*
           * "You already have a request on this hiive" is information, not a
           * failure — and the useful half of it is WHICH request. Show them
           * that one rather than leaving them on a grid arguing with a toast.
           */
          const openRequestId = existingRequestId(result);
          if (openRequestId !== null) {
            toast(result.message || "You already have a request here");
            setRequestSheet(null);
            router.push(`/membership-requests/${openRequestId}`);
            return "requested";
          }

          toast.error(result.message || "Failed to continue");
          return "failed";
        }

        if (requiresApproval) {
          const requestId =
            result.data &&
            typeof result.data === "object" &&
            "id" in result.data &&
            typeof result.data.id === "number"
              ? result.data.id
              : null;

          captureClientEvent(ANALYTICS_EVENTS.TIER_JOIN_REQUESTED, {
            ...analyticsProperties,
            requestId,
            deduplicated: alreadyPending(result),
          });
          const destination = requestId
            ? `/membership-requests/${requestId}`
            : "/membership-requests";

          /*
           * No success toast before a navigation.
           *
           * The request page IS the confirmation — it says who has it, what
           * they asked for and by when. A toast on the way out just means one
           * tap reads as two events, and it is gone by the time the
           * destination paints. The one thing worth saying here is the thing
           * the destination CANNOT say: that the message they typed did not
           * make it.
           */
          if (noteDropped(result)) {
            toast("Request sent — we couldn't attach your message", {
              description: "You can add it again from the request page.",
            });
          }

          // Close the confirm sheet before anything else opens, so two
          // drawers never animate over each other.
          setRequestSheet(null);

          // Ask for an address only AFTER the request exists. Asking first made
          // the email a toll gate in front of the thing they came to do; asking
          // now means declining costs them nothing, and adding one later still
          // works — the approval parks in the outbox until an address arrives.
          if (!hasDeliverableEmail) {
            emailHandoffDone.current = false;
            setPendingEmailDestination(destination);
            return "requested";
          }

          router.push(destination);
          return "requested";
        }

        const checkout = getRazorpaySubscriptionCheckout(result.data);
        if (checkout) {
          router.push(
            `/hiives/${hiiveSlug}/join/payment?membershipId=${encodeURIComponent(
              checkout.membershipId,
            )}`,
          );
          return "payment";
        }

        setShowConfetti(true);
        toast.success(
          checkout ? "Subscription activated." : "You joined successfully.",
        );
        captureClientEvent(
          ANALYTICS_EVENTS.TIER_JOIN_SUCCEEDED,
          analyticsProperties,
        );

        // A tier that admits instantly and unlocks the event they came from has
        // nothing left to say — send them to the thing they wanted, not back to
        // a price grid they are done with.
        if (eventContext?.requiredTierIds.includes(tierId)) {
          router.push(eventContext.eventHref);
          return "joined";
        }

        router.refresh();
        return "joined";
      } catch (error) {
        logger.error("Join error:", error);
        captureClientEvent(ANALYTICS_EVENTS.TIER_JOIN_FAILED, {
          ...analyticsProperties,
          reason: error instanceof Error ? error.message : "unknown_error",
        });
        toast.error("Failed to continue");
        return "failed";
      }
    },
    [
      eventContext,
      hasDeliverableEmail,
      hiiveId,
      hiiveSlug,
      membershipState,
      router,
      tiers,
    ],
  );

  /**
   * The gate in front of {@link runJoin}, in the order the viewer experiences
   * the walls: identity, then profile, then "can we actually tell you when this
   * is approved".
   *
   * Each branch stashes the pick before it hands control away. Web OAuth is a
   * document navigation, so nothing in React state survives it — without the
   * stash somebody comes back to five tiers having forgotten which one the
   * event needed, which is the exact confusion the highlight exists to prevent.
   */
  const handleJoin = useCallback(
    async (tierId: number, tierPriceId: number): Promise<JoinOutcome> => {
      if (!isAuthenticated) {
        stashCheckoutIntent<TierJoinIntent>({
          kind: "tier-join",
          key: intentKey,
          payload: { tierId, tierPriceId },
        });
        const tierName = tiers.find((tier) => tier.id === tierId)?.name;
        openAuthPrompt({
          title: "Sign in to join",
          description: eventContext
            ? `We'll bring you straight back to ${tierName ?? "this tier"} so you can request your spot at ${eventContext.eventName}.`
            : `Create or sign in to your Hiive account to join ${hiiveName}.`,
          callbackURL: currentHref,
        });
        return "failed";
      }

      // Booking is gated on identity alone; tiers are not. A membership shows
      // this person to the rest of the hiive, so it needs a real profile.
      if (!profileComplete) {
        stashCheckoutIntent<TierJoinIntent>({
          kind: "tier-join",
          key: intentKey,
          payload: { tierId, tierPriceId },
        });
        openAuthPrompt({ intent: "finish-profile", callbackURL: currentHref });
        return "failed";
      }

      const selectedTier = tiers.find((tier) => tier.id === tierId);

      // An approval-gated tier is the one case where the person has something
      // to say and someone to say it to, so it gets a confirm step with a
      // message box. A tier that admits instantly has no reader and no wait —
      // interrupting that would be ceremony for its own sake.
      if (selectedTier?.requiresApproval) {
        setRequestSheet({ tierId, tierPriceId, tierName: selectedTier.name });
        return "failed";
      }

      return runJoin(tierId, tierPriceId);
    },
    [
      currentHref,
      eventContext,
      hiiveName,
      intentKey,
      isAuthenticated,
      openAuthPrompt,
      profileComplete,
      runJoin,
      tiers,
    ],
  );

  /**
   * Pick up where the sign-in left off.
   *
   * Deliberately NOT auto-submitting: `runJoin` on mount would create a join
   * request nobody re-confirmed, and a person who signed in and changed their
   * mind deserves the chance. So the resume scrolls the remembered tier into
   * view and highlights it, and the tap is still theirs to make.
   */
  const [resumedTierId, setResumedTierId] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !profileComplete) return;
    const intent = consumeCheckoutIntent<TierJoinIntent>({
      kind: "tier-join",
      key: intentKey,
    });
    if (!intent) return;
    setResumedTierId(intent.tierId);
  }, [intentKey, isAuthenticated, profileComplete]);

  /**
   * Put the tier that actually matters on screen.
   *
   * Scoped to `gridRef` rather than `document`: Next keeps the route you left
   * mounted under `display: none`, so a document-wide query can happily scroll
   * a card on the previous page forever.
   */
  const scrollTarget =
    resumedTierId ?? eventContext?.requiredTierIds[0] ?? null;

  useEffect(() => {
    if (scrollTarget == null) return;
    const grid = gridRef.current;
    if (!grid) return;
    const card = grid.querySelector<HTMLElement>(
      `[data-tier-id="${scrollTarget}"]`,
    );
    if (!card) return;

    // One frame after paint, so the grid has its final height and the browser
    // does not land the card halfway up a still-growing page.
    const frame = requestAnimationFrame(() => {
      const box = card.getBoundingClientRect();
      const alreadyVisible =
        box.top >= 0 && box.top < window.innerHeight * 0.75;

      // Reordering usually puts the answer on screen already. Scrolling anyway
      // centres the card and pushes the context banner — the thing that
      // explains WHY this tier is highlighted — off the top of the viewport.
      if (alreadyVisible) return;

      card.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "center",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollTarget]);

  /**
   * Leave the email ask exactly once, whichever of its three exits fires
   * first. The ref is the guard rather than the state, because the callbacks
   * all run inside one render and would each see the same stale value.
   */
  const finishEmailHandoff = useCallback(() => {
    if (emailHandoffDone.current) return;
    emailHandoffDone.current = true;
    const destination = pendingEmailDestination;
    setPendingEmailDestination(null);
    if (destination) router.push(destination);
  }, [pendingEmailDestination, router]);

  const handleCancelPending = () => {
    if (!pendingJoinRequest?.id) return;

    captureClientEvent(ANALYTICS_EVENTS.TIER_JOIN_REQUEST_CANCEL_ATTEMPTED, {
      hiiveId,
      hiiveSlug,
      requestId: pendingJoinRequest.id,
      tierName: pendingJoinRequest.tierName,
    });
    startCancelling(() => {
      void (async () => {
        try {
          const result = await cancelJoinRequest({
            requestId: pendingJoinRequest.id,
          });
          if (!result.ok) {
            captureClientEvent(
              ANALYTICS_EVENTS.TIER_JOIN_REQUEST_CANCEL_FAILED,
              {
                hiiveId,
                hiiveSlug,
                requestId: pendingJoinRequest.id,
                reason: result.message ?? "unknown",
              },
            );
            toast.error(result.message || "Failed to cancel request");
            return;
          }

          captureClientEvent(
            ANALYTICS_EVENTS.TIER_JOIN_REQUEST_CANCEL_SUCCEEDED,
            {
              hiiveId,
              hiiveSlug,
              requestId: pendingJoinRequest.id,
            },
          );
          toast.success("Join request canceled");
          router.refresh();
        } catch (error) {
          logger.error("Cancel join request error:", error);
          captureClientEvent(ANALYTICS_EVENTS.TIER_JOIN_REQUEST_CANCEL_FAILED, {
            hiiveId,
            hiiveSlug,
            requestId: pendingJoinRequest.id,
            reason: error instanceof Error ? error.message : "unknown_error",
          });
          toast.error("Failed to cancel request");
        }
      })();
    });
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 h-[55vh] bg-[radial-gradient(ellipse_at_center_top,rgba(106,51,255,0.18),transparent_70%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 bottom-0 h-[35vh] bg-[radial-gradient(ellipse_at_center_bottom,rgba(106,51,255,0.06),transparent_70%)]"
      />

      {showConfetti && (
        <div className="pointer-events-none fixed inset-0 z-1000">
          <ConfettiEffect />
        </div>
      )}

      <PhoneVerificationDrawer
        open={pendingPhoneVerification !== null}
        onOpenChange={(open) => {
          if (!open) setPendingPhoneVerification(null);
        }}
        onVerified={() => {
          const pending = pendingPhoneVerification;
          setPendingPhoneVerification(null);
          if (pending) {
            void handleJoin(pending.tierId, pending.tierPriceId);
          }
        }}
        title="Verify your mobile number to pay"
      />

      {requestSheet ? (
        <TierRequestSheet
          open
          onOpenChange={(next) => {
            if (!next && !isSendingRequest) setRequestSheet(null);
          }}
          tierName={requestSheet.tierName}
          hiiveName={hiiveName}
          eventName={
            eventContext?.requiredTierIds.includes(requestSheet.tierId)
              ? eventContext.eventName
              : null
          }
          contactEmail={hasDeliverableEmail ? contactEmail : null}
          isSubmitting={isSendingRequest}
          onSubmit={(reason) => {
            const pending = requestSheet;
            /*
             * An ASYNC transition, so `isSendingRequest` spans the action AND
             * the navigation started inside it. The previous version was a
             * boolean set before the call and cleared in a `.finally`, which
             * tied the entire loading state to one promise settling — and
             * left the sheet stuck on "Sending" the day that promise took
             * longer than anyone was willing to wait.
             *
             * `runJoin` never rejects (it catches and toasts), so there is
             * nothing to unwind here; the sheet is closed by whichever branch
             * of `runJoin` succeeds, and a failure deliberately leaves it open
             * so the message they typed is still there to retry with.
             */
            startSendingRequest(async () => {
              await runJoin(pending.tierId, pending.tierPriceId, reason);
            });
          }}
        />
      ) : null}

      {/*
        One cover owns the whole exit.

        Between "Send request" and the request page there are two waits — the
        action, then the route — and the sheet could only ever cover the
        first. This covers both, and after `HANDOFF_STALL_MS` it stops being a
        spinner and becomes a door: whatever went wrong, the request has very
        likely been created, and the list is the place to find out.
      */}
      {isSendingRequest ? (
        <div className="fixed inset-0 z-[1100] flex flex-col items-center justify-center gap-4 bg-background/85 px-8 text-center backdrop-blur-sm">
          <div className="size-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          <p className="text-sm font-medium text-text-primary">
            {handoffStalled ? "Still working on it" : "Sending your request"}
          </p>
          {handoffStalled ? (
            <>
              <p className="max-w-xs text-xs leading-relaxed text-balance text-text-secondary">
                This is taking longer than usual. Your request has most likely
                gone through — you can check on it now, and nothing will be sent
                twice.
              </p>
              <Link
                href="/membership-requests"
                className="text-sm font-semibold text-brand underline underline-offset-4"
              >
                View your requests
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      {/*
        Asked AFTER the request is safely created, and always skippable. The
        request works without it — the approval lands in the app either way, and
        the outbox holds the email until an address exists — so the copy
        promises what an address adds rather than threatening what it withholds.
      */}
      <ContactEmailDrawer
        open={pendingEmailDestination !== null}
        /*
         * All three callbacks funnel through one guarded hand-off.
         *
         * The form calls `onOpenChange(false)` and THEN `onVerified`/`onSkip`,
         * both in the same render, so each of the three used to read the same
         * un-updated `pendingEmailDestination` and push the same route twice —
         * two history entries for one tap, and a back button that appeared not
         * to work.
         */
        onOpenChange={(open) => {
          if (open) return;
          finishEmailHandoff();
        }}
        context="tier_join_request"
        title="Request sent. Where should we send the answer?"
        description={`${hiiveName} will review it. Add an email and we'll tell you the moment you're approved — with a link straight to what it unlocks.`}
        onVerified={finishEmailHandoff}
        skipLabel="Skip — notify me in the app"
        skipNote={
          <>
            You'll still get an in-app notification. An email just means you
            find out even if you're not in the app that day.
          </>
        }
        onSkip={finishEmailHandoff}
      />

      {/* Header */}
      <header
        className="sticky top-0 z-20 border-b border-glass-border-05 bg-background/50 backdrop-blur-2xl"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto flex w-full max-w-6xl items-center gap-3 px-4 py-3">
          <Link
            ref={backRef}
            href={backHref}
            aria-label="Back to hiive"
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-glass-border-08 bg-glass-bg-04 text-text-secondary shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-xl transition-colors hover:border-border-strong hover:bg-glass-bg-08 hover:text-text-primary dark:hover:border-white/15"
          >
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[17px] font-semibold tracking-tight text-text-primary">
              {hiiveName}
            </h1>
            <p className="truncate text-xs text-text-tertiary">
              {getPageDescription()}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-6">
        {eventContext && !previewMode ? (
          <EventContextBanner
            eventContext={eventContext}
            isAuthenticated={isAuthenticated}
          />
        ) : null}

        {previewMode ? (
          <div className="space-y-5">
            <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text-secondary">
              <span className="font-semibold text-text-primary">
                Organiser preview.
              </span>{" "}
              This is how a new visitor sees the available memberships and
              programmes. Joining is disabled in preview mode.
            </div>
            {tiers.length === 0 ? (
              <NoTiersEmptyState
                hiiveName={hiiveName}
                backHref={backHref}
                previewMode
              />
            ) : (
              <div className={tierGridClass(tiers.length)}>
                {tiers.map((tier) => (
                  <TierCard
                    key={tier.id}
                    tier={tier}
                    onJoin={handleJoin}
                    currentMembership={undefined}
                    membershipState="not_member"
                    hiiveSlug={hiiveSlug}
                    allowSelection={false}
                    disabledLabel="Preview only"
                  />
                ))}
              </div>
            )}
          </div>
        ) : membershipState === "paid_member" ? (
          <PaidMemberView
            hiiveName={hiiveName}
            hiiveSlug={hiiveSlug}
            nextSteps={nextSteps}
          />
        ) : membershipState === "pending_member" ? (
          <PendingMemberView
            pendingJoinRequest={pendingJoinRequest ?? null}
            onCancel={pendingJoinRequest ? handleCancelPending : undefined}
            isCancelling={isCancelling}
          />
        ) : membershipState === "free_member" ? (
          <FreeMemberView
            tierName={
              tiers.find((tier) => tier.id === currentMembership?.tierId)?.name
            }
            hiiveName={hiiveName}
            hiiveSlug={hiiveSlug}
            nextSteps={nextSteps}
          />
        ) : orderedTiers.length === 0 ? (
          <NoTiersEmptyState hiiveName={hiiveName} backHref={backHref} />
        ) : (
          <div ref={gridRef} className={tierGridClass(orderedTiers.length)}>
            {orderedTiers.map((tier) => (
              <TierCard
                key={tier.id}
                tier={tier}
                onJoin={handleJoin}
                currentMembership={currentMembership}
                membershipState={membershipState}
                hiiveSlug={hiiveSlug}
                eventRelevance={
                  eventContext
                    ? requiredTierIds.has(tier.id)
                      ? "required"
                      : "excluded"
                    : null
                }
                eventName={eventContext?.eventName ?? null}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );

  function getPageDescription() {
    if (previewMode) {
      return "Preview available memberships and programmes.";
    }
    switch (membershipState) {
      case "not_member":
        return "Choose a membership or programme and request to join.";
      case "free_member_upgrade_available":
        return "Upgrade to a paid plan to unlock additional features.";
      case "payment_pending":
        return "Complete payment to activate your membership.";
      case "paid_member":
        return "View your active membership details.";
      case "pending_member":
        return "Your request is awaiting organiser review.";
      case "free_member":
        return "Your membership or programme is active.";
      default:
        return "Choose a membership or programme and request to join.";
    }
  }
}

/**
 * Column count that matches the number of tiers there actually are.
 *
 * A fixed three-column grid is right for a hiive that sells five plans and
 * wrong for one that sells one: an invite-only hiive with a single tier
 * rendered one narrow card marooned in the top-left third of a desktop page,
 * with two empty columns beside it implying something had failed to load.
 */
function tierGridClass(count: number) {
  if (count <= 1) {
    return "mx-auto grid w-full max-w-md gap-4 sm:gap-6";
  }
  if (count === 2) {
    return "mx-auto grid w-full max-w-3xl gap-4 sm:gap-6 md:grid-cols-2";
  }
  return "grid gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-3";
}

/**
 * A hiive with no active tiers used to render a header over a blank page —
 * which reads as a failed load. Name the state explicitly, keep it calm, and
 * give them somewhere to go.
 */
function NoTiersEmptyState({
  hiiveName,
  backHref,
  previewMode = false,
}: {
  hiiveName: string;
  backHref: string;
  previewMode?: boolean;
}) {
  return (
    <section
      aria-label="No memberships available"
      className="mx-auto flex w-full max-w-md flex-col items-center rounded-2xl border border-glass-border-08 bg-glass-bg-04 px-6 py-12 text-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-xl"
    >
      <div className="flex size-14 items-center justify-center rounded-full bg-brand/10">
        <Users className="size-6 text-brand" aria-hidden />
      </div>
      <h2 className="mt-4 text-[17px] font-semibold tracking-tight text-text-primary">
        No memberships available yet
      </h2>
      <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-balance text-text-secondary">
        {previewMode
          ? `${hiiveName} hasn't published any memberships or programmes yet. Create a tier and it will appear here.`
          : `${hiiveName} hasn't opened any memberships or programmes yet. Check back soon — you can still explore the hiive.`}
      </p>
      <Link
        href={backHref}
        className="mt-5 inline-flex h-9 items-center rounded-full bg-brand px-4 text-sm font-medium text-white transition-colors hover:bg-brand-hover"
      >
        Back to {hiiveName}
      </Link>
    </section>
  );
}

/**
 * "Here is why you are on this page."
 *
 * A viewer who tapped through from a shared event link has no other reason to
 * be looking at a price grid, and the single most expensive mistake available
 * to them is buying a tier that does not include the thing they came for. So
 * the banner names the event, names the tier, and stays on screen above the
 * cards rather than being a toast that has already gone by the time they read
 * the prices.
 */
function EventContextBanner({
  eventContext,
  isAuthenticated,
}: {
  eventContext: JoinEventContext;
  isAuthenticated: boolean;
}) {
  const tierList = eventContext.requiredTierNames;
  const tierPhrase =
    tierList.length === 0
      ? null
      : tierList.length === 1
        ? tierList[0]
        : `${tierList.slice(0, -1).join(", ")} or ${tierList.at(-1)}`;

  return (
    <section
      aria-label="Why you're here"
      className="mb-5 overflow-hidden rounded-2xl border border-brand/30 bg-brand/[0.06] dark:bg-brand/[0.09]"
    >
      <div className="flex items-start gap-3 p-4">
        {eventContext.bannerUrl ? (
          <Image
            src={eventContext.bannerUrl}
            alt=""
            width={64}
            height={64}
            // Fixed box + object-cover: the media is reserved before it loads,
            // so nothing under it moves when it arrives.
            className="size-16 shrink-0 rounded-xl object-cover"
            unoptimized
          />
        ) : (
          <div className="flex size-16 shrink-0 items-center justify-center rounded-xl bg-brand/15">
            <Ticket className="size-6 text-brand" aria-hidden />
          </div>
        )}

        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs font-semibold tracking-wide text-brand uppercase">
            Members-only event
          </p>
          <h2 className="text-[15px] leading-snug font-semibold text-text-primary">
            {eventContext.eventName}
          </h2>
          {eventContext.whenLabel ? (
            <p className="flex items-center gap-1.5 text-xs text-text-secondary">
              <CalendarDays className="size-3.5 shrink-0" aria-hidden />
              {eventContext.whenLabel}
            </p>
          ) : null}
        </div>
      </div>

      <div className="border-t border-brand/20 px-4 py-3">
        <p className="text-[13px] leading-relaxed text-text-primary">
          {tierPhrase ? (
            <>
              Only the{" "}
              <span className="font-semibold text-brand">{tierPhrase}</span>{" "}
              {tierList.length > 1 ? "tiers get" : "tier gets"} you in.
            </>
          ) : (
            <>This event is limited to specific tiers.</>
          )}{" "}
          {isAuthenticated ? null : (
            <span className="text-text-secondary">
              You can pick it now — we'll sign you in and bring you straight
              back here.
            </span>
          )}
        </p>
        <Link
          href={eventContext.eventHref}
          className="mt-2 inline-flex text-xs text-brand underline underline-offset-4"
        >
          Back to the event
        </Link>
      </div>
    </section>
  );
}
